import { Component, onDidLoad, onDidUnload, useRef } from '@atomify/hooks';
import {
    FRCWidgetCompleteEvent,
    FRCWidgetWidgetErrorEvent,
    StartMode,
    WidgetHandle,
} from '@friendlycaptcha/sdk';
import { nextEvent } from '@utilities/next-event';

import {
    FC_FIELD_NAME,
    FC_WIDGET_COMPLETE,
    FC_WIDGET_ERROR,
    FC_WIDGET_EXPIRE,
    FC_WIDGET_HOOK,
    FriendlyCaptcha,
} from '../constants/friendly-captcha';

interface FriendlyCaptchaHookProps {
    formParent: Component | HTMLElement;
    renderOnLoad?: boolean;
    startMode?: StartMode;
}

export const useFriendlyCaptcha = ({
    formParent,
    renderOnLoad = false,
    startMode = 'focus',
}: FriendlyCaptchaHookProps) => {
    const widget = useRef<WidgetHandle | null>(null);

    async function getFcToken(): Promise<string> {
        if (!widget.current || widget.current.isDestroyed) {
            throw new Error('FriendlyCaptcha.Widget.NotInitialized');
        }

        const state = widget.current.getState();

        if (state === 'completed') return widget.current.getResponse();
        if (state === 'error') throw new Error('FriendlyCaptcha.Widget.ValidationFailed');

        const { type, detail } = await nextEvent<
            FRCWidgetCompleteEvent | FRCWidgetWidgetErrorEvent
        >(widget.current, FC_WIDGET_COMPLETE, FC_WIDGET_ERROR);

        if (type === FC_WIDGET_COMPLETE) {
            return detail.response;
        }

        throw new Error('FriendlyCaptcha.Widget.ValidationFailed');
    }

    const isFcReady = () => widget.current?.getState() === 'completed';

    const handleExpire = () => widget.current?.reset();

    function init() {
        const element = formParent.querySelector<HTMLElement>(`[${FC_WIDGET_HOOK}]`);
        if (!element) {
            console.error('[FriendlyCaptcha] Unable to locate widget element in rendered form.');
            return;
        }

        if (widget.current) remove();

        widget.current = FriendlyCaptcha.createWidget({
            element,
            formFieldName: FC_FIELD_NAME,
            sitekey: window.__ENVIRONMENT_DETAILS__.friendlyCaptchaSiteKey,
            startMode,
        });

        widget.current.addEventListener(FC_WIDGET_EXPIRE, handleExpire);
    }

    function refresh() {
        if (!widget.current) {
            init();
            return;
        }

        widget.current.reset();
    }

    function remove() {
        if (!widget.current) return;
        widget.current.removeEventListener(FC_WIDGET_EXPIRE, handleExpire);
        widget.current.destroy();
        widget.current = null;
    }

    onDidLoad(() => {
        if (renderOnLoad) init();
    });

    onDidUnload(remove);

    return {
        getFcToken,
        isFcReady,
        initFcWidget: init,
        removeFcWidget: remove,
        refreshFcWidget: refresh,
    };
};
